/**
 * Frontend functionality for the carousel block using Swiper.js
 */

// Import Swiper and required modules
import { Swiper } from 'swiper';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';

class UKitCarousel {
	constructor(element) {
		this.element = element;
		this.settings = this.getSettings();
		this.init();
	}

	getSettings() {
		const element = this.element;
		return {
			// Basic settings (default for mobile < 480px)
			slidesPerView: 1, // Always 1 slide on mobile
			spaceBetween: Math.min(parseInt(element.dataset.spaceBetween) || 0, 10), // Limit spacing on mobile
			
			// Autoplay
			autoplay: element.dataset.autoplay === 'true' ? {
				delay: parseInt(element.dataset.autoplaySpeed) || 3000,
				pauseOnMouseEnter: element.dataset.pauseOnHover === 'true',
				disableOnInteraction: false,
			} : false,
			
			// Loop/Infinite
			loop: element.dataset.infinite === 'true',
			
			// Navigation arrows (will be set in init method)
			navigation: element.dataset.showArrows === 'true',
			
			// Pagination dots (will be set in init method)
			pagination: element.dataset.showDots === 'true',
			
			// Responsive breakpoints
			breakpoints: {
				// Mobile (up to 480px) - always 1 slide
				480: {
					slidesPerView: 1,
					spaceBetween: Math.min(parseInt(element.dataset.spaceBetween) || 0, 10),
				},
				// Small tablet (480px+) - maximum 2 slides
				640: {
					slidesPerView: Math.min(parseInt(element.dataset.slidesToShow) || 1, 2),
					spaceBetween: parseInt(element.dataset.spaceBetween) || 0,
				},
				// Tablet (768px+) - maximum 3 slides
				768: {
					slidesPerView: Math.min(parseInt(element.dataset.slidesToShow) || 1, 3),
					spaceBetween: parseInt(element.dataset.spaceBetween) || 0,
				},
				// Desktop small (1024px+) - maximum 4 slides
				1024: {
					slidesPerView: Math.min(parseInt(element.dataset.slidesToShow) || 1, 4),
					spaceBetween: parseInt(element.dataset.spaceBetween) || 0,
				},
				// Desktop medium (1200px+) - maximum 6 slides
				1200: {
					slidesPerView: Math.min(parseInt(element.dataset.slidesToShow) || 1, 6),
					spaceBetween: parseInt(element.dataset.spaceBetween) || 0,
				},
				// Desktop large (1400px+) - full number of slides
				1400: {
					slidesPerView: parseInt(element.dataset.slidesToShow) || 1,
					spaceBetween: parseInt(element.dataset.spaceBetween) || 0,
				},
			},
			
			// Accessibility
			a11y: {
				enabled: true,
				prevSlideMessage: 'Previous slide',
				nextSlideMessage: 'Next slide',
				firstSlideMessage: 'This is the first slide',
				lastSlideMessage: 'This is the last slide',
			},
			
			// Smooth transitions
			speed: 300,
			
			// Allow touch/swipe
			touchRatio: 1,
			touchAngle: 45,
			grabCursor: true,
			
			// Keyboard navigation
			keyboard: {
				enabled: true,
				onlyInViewport: true,
			},
			
			// Mouse wheel
			mousewheel: {
				enabled: false, // Can be enabled if needed
			},
		};
	}

	init() {
		// Get swiper container
		this.swiperContainer = this.element.querySelector('.swiper');
		
		if (!this.swiperContainer) {
			return;
		}

		// Update navigation and pagination selectors to use swiper container
		const settings = { ...this.settings };
		
		if (settings.navigation) {
			const nextEl = this.swiperContainer.querySelector('.swiper-button-next');
			const prevEl = this.swiperContainer.querySelector('.swiper-button-prev');
			
			settings.navigation = {
				nextEl,
				prevEl,
			};
		}
		
		if (settings.pagination) {
			const paginationEl = this.swiperContainer.querySelector('.swiper-pagination');
			
			settings.pagination = {
				el: paginationEl,
				clickable: true,
				dynamicBullets: true,
			};
		}

		// Initialize Swiper
		this.swiper = new Swiper(this.swiperContainer, {
			modules: [Navigation, Pagination, Autoplay],
			...settings,
		});

		// Add event listeners
		this.bindEvents();
	}

	bindEvents() {
		// Pause autoplay on hover if enabled
		if (this.settings.autoplay && this.settings.autoplay.pauseOnMouseEnter) {
			this.element.addEventListener('mouseenter', () => {
				this.swiper.autoplay.pause();
			});

			this.element.addEventListener('mouseleave', () => {
				this.swiper.autoplay.resume();
			});
		}

		// Handle visibility change (pause when tab is hidden)
		document.addEventListener('visibilitychange', () => {
			if (this.settings.autoplay) {
				if (document.hidden) {
					this.swiper.autoplay.pause();
				} else {
					this.swiper.autoplay.resume();
				}
			}
		});
	}

	// Public methods for external control
	goToSlide(index) {
		this.swiper.slideTo(index);
	}

	nextSlide() {
		this.swiper.slideNext();
	}

	prevSlide() {
		this.swiper.slidePrev();
	}

	pauseAutoplay() {
		if (this.swiper.autoplay) {
			this.swiper.autoplay.pause();
		}
	}

	resumeAutoplay() {
		if (this.swiper.autoplay) {
			this.swiper.autoplay.resume();
		}
	}

	destroy() {
		if (this.swiper) {
			this.swiper.destroy();
		}
	}
}

// Initialize all carousels when DOM is ready
function initCarousels() {
	const carousels = document.querySelectorAll('.wp-block-u-kit-carousel, .u-kit-carousel');
	
	carousels.forEach(carousel => {
		// Skip if already initialized
		if (carousel.ukitCarousel) {
			return;
		}
		
		// Initialize carousel
		carousel.ukitCarousel = new UKitCarousel(carousel);
	});
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initCarousels);
} else {
	initCarousels();
}

// Reinitialize when new blocks are added (for block editor)
if (typeof window !== 'undefined' && window.wp && window.wp.domReady) {
	window.wp.domReady(initCarousels);
}

// Export for external access
window.UKitCarousel = UKitCarousel;